import React, { useState } from 'react';
import axios from 'axios';
import { localizeReference } from '../utils/bookTranslations';
import { Search, Sparkles, Loader2, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fuzzy ("Smart") Bible search dialog — type a plain-language description
 * ("the verse about building a house on sand") and get up to 5 references
 * back. Each result is clickable: clicking jumps the BibleReader to the
 * verse via the existing `lumera:verse-select` event.
 */
const FuzzySearchInput = ({ language, onClose }) => {
  const isES = language === 'es';
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);

  const handleSearch = async () => {
    const q = query.trim();
    if (q.length < 3) return;
    setLoading(true);
    setMatches([]);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/ai/fuzzy-search`,
        { query: q, language: isES ? 'es' : 'en' },
        { withCredentials: true },
      );
      setMatches(res.data?.matches || []);
      if (!res.data?.matches?.length) {
        toast.message(isES ? 'Sin coincidencias' : 'No matches found');
      }
    } catch (e) {
      console.error('Fuzzy search failed:', e);
      toast.error(isES ? 'Búsqueda no disponible' : 'Search unavailable');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMatch = (ref) => {
    window.dispatchEvent(new CustomEvent('lumera:verse-select', { detail: { reference: ref } }));
    onClose?.();
  };

  return (
    <div className="space-y-3" data-testid="fuzzy-search-input">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-purple-600 shrink-0" />
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder={isES
            ? 'Ej: el versículo sobre construir casa en la roca'
            : 'e.g. the verse about building a house on the rock'}
          data-testid="fuzzy-search-query"
          className="flex-1"
        />
        <Button
          onClick={handleSearch}
          disabled={loading || query.trim().length < 3}
          size="sm"
          data-testid="fuzzy-search-submit"
          className="bg-gradient-to-r from-purple-600 to-teal-600 text-white hover:opacity-90"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </div>

      {matches.length > 0 && (
        <div className="space-y-2" data-testid="fuzzy-search-results">
          {matches.map((m, i) => (
            <button
              key={i}
              onClick={() => handleSelectMatch(m.reference)}
              data-testid={`fuzzy-search-result-${i}`}
              className="w-full text-left flex items-center justify-between gap-3 p-2 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-950/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-purple-700 dark:text-purple-300">{localizeReference(m.reference, isES)}</div>
                {m.why && (
                  <div className="text-xs text-slate-600 dark:text-slate-400 truncate">{m.why}</div>
                )}
              </div>
              <ArrowRight className="w-4 h-4 text-purple-600 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Smart Synopsis card — one-tap chapter or book summary. Cached server-side
 * so repeated taps are instant.
 */
const SmartSynopsisCard = ({ book, chapter, language, scope = 'chapter' }) => {
  const isES = language === 'es';
  const [text, setText] = useState('');
  const [textLang, setTextLang] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleLoad = async () => {
    if (open) { setOpen(false); return; }          // tapping the trigger again closes it
    if (text && textLang === language) { setOpen(true); return; }  // reuse only same-language
    if (!book || (scope === 'chapter' && !chapter)) return;
    setLoading(true);
    setOpen(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/ai/synopsis`,
        { book, chapter: scope === 'chapter' ? chapter : null, scope, language },
        { withCredentials: true },
      );
      setText(res.data?.synopsis || '');
      setTextLang(language);
    } catch (e) {
      console.error('Synopsis failed:', e);
      toast.error(isES ? 'Resumen no disponible' : 'Synopsis unavailable');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const label = scope === 'book'
    ? (isES ? 'Resumen del libro' : 'Book synopsis')
    : (isES ? 'Resumen del capítulo' : 'Chapter synopsis');

  return (
    <div data-testid={`synopsis-card-${scope}`}>
      <Button
        onClick={handleLoad}
        variant="outline"
        size="sm"
        data-testid={`synopsis-trigger-${scope}`}
        className="border-teal-500 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-950"
      >
        <Sparkles className="w-3.5 h-3.5 mr-2" />
        {label}
      </Button>
      {open && (
        <Card className="mt-2 border-l-4 border-teal-500 bg-teal-50/40 dark:bg-teal-950/20" data-testid={`synopsis-body-${scope}`}>
          <CardContent className="p-3 text-sm leading-relaxed text-slate-700 dark:text-slate-300 relative">
            {loading ? (
              <div className="flex items-center gap-2 text-teal-700 dark:text-teal-300 text-xs">
                <Loader2 className="w-4 h-4 animate-spin" />
                {isES ? 'Generando…' : 'Generating…'}
              </div>
            ) : (
              <>
                <button
                  onClick={() => setOpen(false)}
                  className="absolute top-2 right-2 p-1 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-200 dark:hover:bg-slate-700"
                  aria-label="Close"
                  data-testid={`synopsis-close-${scope}`}
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="pr-6">{text}</div>
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setOpen(false)}
                    data-testid={`synopsis-close-btn-${scope}`}
                    className="h-7 text-xs text-teal-700 dark:text-teal-300"
                  >
                    {isES ? 'Cerrar' : 'Close'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export { FuzzySearchInput, SmartSynopsisCard };
export default FuzzySearchInput;
